import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { API, trackEvent, formatApiError, getCoachRef } from "@/lib/api";

const ClaimForm = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const plan = params.get("plan") || "unknown";

  const claim = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const { data } = await axios.post(`${API}/members/claim`, { email, plan, coach: getCoachRef() || undefined });
      trackEvent("membership_claimed", { plan });
      if (data.referred_by) toast.success(`Referred by ${data.referred_by} — they'll hear about it.`);
      navigate(`/members?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err));
    }
  };

  return (
    <form onSubmit={claim} className="mt-8" data-testid="success-claim-form">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email used at checkout"
          data-testid="success-claim-email"
          aria-label="Email used at checkout"
          className="flex-1 bg-white/5 border border-white/15 px-4 py-3.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-volt transition-colors"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="success-claim-button"
          className="bg-volt text-obsidian font-display font-bold px-6 py-3.5 hover:bg-white transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2 shrink-0"
        >
          {status === "loading" ? "Unlocking..." : "Unlock My Assets"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      {error && <p data-testid="success-claim-error" className="text-red-400 text-xs mt-3">{error}</p>}
    </form>
  );
};

export default function Success() {
  useEffect(() => {
    document.title = "Welcome to Membership — Wired 2 Win Golf";
    trackEvent("membership_success_view");
  }, []);

  return (
    <main data-testid="success-page" className="min-h-[100svh] flex items-center justify-center px-6 pt-20 bg-obsidian">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg border border-volt/40 bg-ink p-10 sm:p-12 text-center"
      >
        <span className="inline-flex w-16 h-16 bg-volt items-center justify-center mb-8">
          <Check className="w-8 h-8 text-obsidian" />
        </span>
        <p className="text-xs tracking-[0.25em] uppercase font-bold text-volt mb-3">Payment received</p>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl uppercase leading-[0.95]">
          You're <span className="text-volt">in.</span>
        </h1>
        <p className="mt-6 text-white/60 text-sm sm:text-base leading-relaxed">
          Square is confirming your subscription — your receipt arrives by email shortly. Enter the email you used at checkout to unlock your training assets right away.
        </p>
        <ClaimForm />
        <div className="mt-8 border-t border-white/10 pt-8">
          <Link
            to="/resources"
            data-testid="success-resources-cta"
            onClick={() => trackEvent("cta_click", { location: "success" })}
            className="group inline-flex items-center gap-2 text-volt font-bold text-sm hover:text-white transition-colors"
          >
            Browse the resources meanwhile <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </motion.div>
    </main>
  );
}
